import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { TreeSelectModule } from "primeng/treeselect";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseService } from "@6grain/masofa-monolith-api-client/dictionaries/api.base.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ServerTranslateService } from "@services/server-translate.service";
import { getDeepField } from "@app/utils/_msf";
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'msf-tree-select',
  exportAs: 'msfTreeSelect',
  imports: [
    TreeSelectModule,
    FormsModule,
    TranslatePipe,
  ],
  templateUrl: './tree-select.html',
  styleUrl: './tree-select.scss',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MsfTreeSelect),
      multi: true
    }
  ],
})
export class MsfTreeSelect implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
  $destroy$: Subject<void> = new Subject<void>();

  treeNodes: any[] = [];
  loading: boolean = false;

  // Internal model to hold the full TreeNode object required by PrimeNG
  innerModel: any;

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  @Input() options?: any[] = [];
  @Input() model: any; // Represents the ID/Value from the parent
  @Output() modelChange = new EventEmitter<any>();
  @Output() selectItem = new EventEmitter<any>();
  @Input() placeholder?: string = 'Choose...';
  @Input() optionLabel: string = 'label';
  @Input() translateBy: string | null = 'names';
  @Input() optionValue: string = 'id';
  @Input() treeBy: string = 'parentId';
  @Input() service?: BaseService | any;
  @Input() serviceMethod?: BaseService | any;
  @Input() showClear = false;
  @Input() filter = {};
  @Input() width = '100%';
  @Input() appendTo: any = null;

  constructor(
    private serverTranslateService: ServerTranslateService,
    private translateService: TranslateService,
  ) {}

  // ControlValueAccessor: maps the incoming ID to the corresponding TreeNode
  writeValue(obj: any): void {
    this.model = obj;
    this.restoreSelectionByKey(obj);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Implementation for disabled state if required
  }

  ngOnInit() {
    if (this.service && this.serviceMethod) {
      this.loading = true;
      this.service[this.serviceMethod](this.filter || {})
        .pipe(takeUntil(this.$destroy$))
        .subscribe((data: any) => {
          this.options = data;
          this.treeNodes = this.buildTreeStructure(data);
          this.restoreSelectionByKey(this.model);
          this.loading = false;
        });
    } else if (this.options) {
      this.treeNodes = this.buildTreeStructure(this.options);
      this.restoreSelectionByKey(this.model);
    }

    // Handle language changes to refresh labels without losing selection
    this.translateService.onLangChange
      .pipe(takeUntil(this.$destroy$))
      .subscribe(() => {
        const currentKey = this.model;
        this.treeNodes = this.buildTreeStructure(this.options || []);
        this.restoreSelectionByKey(currentKey);
      });
  }

  // Finds and sets the TreeNode object based on the provided ID (key)
  private restoreSelectionByKey(key: any): void {
    // If the key is null, undefined, or empty, clear the selection
    if (key === null || key === undefined || key === '') {
      this.innerModel = null;
      return;
    }

    const findNodeByKey = (nodes: any[]): any => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children?.length) {
          const found = findNodeByKey(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    const foundNode = findNodeByKey(this.treeNodes);

    // Explicitly set to null if the ID doesn't exist in the new tree
    this.innerModel = foundNode ? foundNode : null;
  }

  ngOnChanges(changes: SimpleChanges) {
    // If options change, rebuild the tree and re-map the selection
    if (changes['options'] && !changes['options'].firstChange) {
      this.treeNodes = this.buildTreeStructure(this.options || []);
      this.restoreSelectionByKey(this.model);
    }

    // If the model itself changes from the outside (parent sets it to null)
    if (changes['model'] && !changes['model'].firstChange) {
      this.restoreSelectionByKey(changes['model'].currentValue);
    }
  }

  // Triggered when an item is selected in the UI
  onSelectItem() {
    // Extract the ID (key) from the internal TreeNode object
    const selectedId = this.innerModel ? this.innerModel.key : null;

    this.model = selectedId;
    this.onChangeCallback(selectedId);
    this.modelChange.emit(selectedId);
    this.selectItem.emit(selectedId);
  }

  ngOnDestroy() {
    this.$destroy$.next();
    this.$destroy$.complete();
  }

  // Transforms flat data into a PrimeNG-compatible tree structure
  buildTreeStructure(items: any[]): any[] {
    if (!items || !Array.isArray(items)) return [];

    const translatedItems = items.map((item: any) => {
      let labelValue: any;
      if (this.translateBy && this.translateBy !== 'none') {
        labelValue = this.serverTranslateService.translate(item, this.translateBy);
      } else {
        labelValue = getDeepField(item, this.optionLabel);
      }

      if (labelValue && typeof labelValue === 'object') {
        labelValue = this.serverTranslateService.formatTranslationLabel(labelValue);
      }

      return {
        ...item,
        label: labelValue?.toString() || '',
        key: getDeepField(item, this.optionValue), // Maps the primary ID to the node key
        data: item
      };
    }).filter(item => this.serverTranslateService.isValidDisplayLabel(item.label));

    const itemMap = new Map();
    const rootItems: any[] = [];

    // Map all nodes first
    translatedItems.forEach(item => itemMap.set(item.key, { ...item, children: [] }));

    // Link children to parents
    translatedItems.forEach(item => {
      const parentId = getDeepField(item, this.treeBy);
      const node = itemMap.get(item.key);

      if (parentId && itemMap.has(parentId) && parentId !== item.key) {
        itemMap.get(parentId).children.push(node);
      } else {
        rootItems.push(node);
      }
    });

    // Sort alphabetically at all levels
    const sortNodes = (nodes: any[]) => {
      nodes.sort((a, b) => a.label.localeCompare(b.label));
      nodes.forEach(n => n.children?.length && sortNodes(n.children));
    };

    sortNodes(rootItems);
    return rootItems;
  }
}
